import React from 'react';
import styled from 'styled-components';
import { QueryType, TimeUnit } from '../../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { IDateInput } from '../../../../../redux/slices/oldBuilder/date/types';
import * as BuilderUI from '../../../../../components/Builder/BuilderUI';
import DatePickerWidget from '../../../../../components/Builder/DatePickerWidget';
import {
  activeSchemaIdSelector,
  builderDateConfigSelector,
  builderDefaultOptionsSelector,
  builderSelectedOptionsSelector,
  selectTimeUnits,
} from '../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../utils/types';
import {
  setActiveCohortUnit,
  setActiveRetentionUnit,
  setBuilderTimeWindowAction,
  setSelectedDateInput,
} from '../../../../../redux/slices/oldBuilder';
import TimeUnitMenu from '../../../../../components/Builder/BuilderFields/TimeUnitMenu';
import { batch } from 'react-redux';
import { builderDateSelector } from '../../../../../redux/selectors/date';
import moment from 'moment';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const CohortUnit = () => {
  const dispatch = useAppDispatch();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const dateInput = useAppSelector(builderDateSelector);
  const dateConfig = useAppSelector(builderDateConfigSelector);
  const currentRetentionUnit = useAppSelector(
    ({ builder }) => builder.retention?.activeRetentionUnit,
  );
  const defaultOptions = useAppSelector((state) =>
    builderDefaultOptionsSelector(state),
  );
  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );
  const timeUnits = useAppSelector(selectTimeUnits);

  assertCondition(
    defaultOptions?.type === QueryType.Retention &&
      selectedOptions?.type === QueryType.Retention,
  );

  const { defaultCohortUnits, defaultRetentionUnits } = defaultOptions;
  const { cohortUnit } = selectedOptions;

  const handleChangeDateRange = (dateInput: IDateInput) => {
    batch(() => {
      dispatch(setSelectedDateInput({ newDateInput: dateInput }));

      // KBT-703
      // if cohort date range is fixed, update timeWindow by liveStartDate - selectedEndDate,
      // if less than 0, set to 0
      if (dateInput.datePicker.__typename === 'DatePickerFixed') {
        const timeWindow = moment(dateConfig.liveStartDate).diff(
          dateInput.datePicker.end,
          'days',
        );
        dispatch(
          setBuilderTimeWindowAction({
            newValue: timeWindow > 0 ? timeWindow : 0,
          }),
        );
      } else {
        dispatch(setBuilderTimeWindowAction({ newValue: 0 }));
      }
    });
  };

  const handleChangeTimeUnit = (newTimeUnit: TimeUnit) => {
    dispatch(setActiveCohortUnit({ newTimeUnit }));

    const newCohortIndex = defaultCohortUnits.indexOf(newTimeUnit);
    const retentionIndex = defaultRetentionUnits.indexOf(currentRetentionUnit);

    if (newCohortIndex > retentionIndex) {
      dispatch(
        setActiveRetentionUnit({
          newTimeUnit,
        }),
      );
    }
  };

  return (
    <Container>
      <BuilderUI.BuilderFieldContainer>
        <BuilderUI.BuilderFieldLabel>Cohort</BuilderUI.BuilderFieldLabel>
        <DatePickerWidget
          endDate={dateConfig.maxDate}
          dateInput={dateInput}
          onChange={handleChangeDateRange}
          schemaId={activeSchemaId}
        />
      </BuilderUI.BuilderFieldContainer>
      <TimeUnitMenu
        value={cohortUnit}
        timeUnits={timeUnits}
        onChange={handleChangeTimeUnit}
      />
    </Container>
  );
};

export default CohortUnit;
